import AIState, { useAIState } from "@/components/AIState";
import { submitAI, refreshAI, storedAI } from "@/lib/aiOperations";
import { useRead, ReadState, isCollection, isTrends } from "@/components/LoadingStates";
import { useAuth } from "@/context/AuthContext";
import SectionTabs, { SECTION_TABS } from "@/components/SectionTabs";
import { useEffect, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wand2, Loader2, Flame, Trash2 } from "lucide-react";
import { classNames, timeAgo } from "@/lib/format";
import RemixResult from "@/components/RemixResult";

export default function HookRemix() {
  const { canEdit, user } = useAuth();
  const ai = useAIState("remix");
  const operationScope = `${user?.id}|${user?.workspace_id || "personal"}`;
  const operationScopeRef = useRef(operationScope); operationScopeRef.current = operationScope;
  const [operationState, setOperationState] = useState(null);
  const operation = operationState?.scope === operationScope ? operationState.data : null;
  const setOperation = (data) => { if (operationScopeRef.current === operationScope) setOperationState({ scope: operationScope, data }); };
  const refreshOperation = () => refreshAI(user, "remix").then(setOperation).catch(() => {});
  const [hook, setHook] = useState("");
  const [niche, setNiche] = useState("");
  const [platform, setPlatform] = useState("any");
  const [loading, setLoading] = useState(false);
  const [currentState, setCurrentState] = useState(null);
  const historyRead = useRead("/hooks/remix", (d) => isCollection(d) && d.every((h) => typeof h.hook === "string" && h.result && typeof h.result === "object"));
  const hotRead = useRead("/trends/weekly?week_offset=0", isTrends);
  const current = currentState?.key === historyRead.key ? currentState.data : null;
  const currentHistoryKey = useRef(historyRead.key); currentHistoryKey.current = historyRead.key;
  const setCurrent = (data) => { if (currentHistoryKey.current === historyRead.key) setCurrentState(data ? { key: historyRead.key, data } : null); };
  const history = historyRead.data || [];
  const hot = hotRead.data?.hooks.slice(0, 5) || [];
  const loadHistory = () => historyRead.reload().catch(() => {});
  useEffect(() => {
    try {
      const pending = storedAI(user, "remix");
      if (pending && pending.state !== "completed" && !pending.can_retry && pending.admission !== "not_admitted") {
        setHook(pending.parameters.hook || ""); setNiche(pending.parameters.niche || ""); setPlatform(pending.parameters.platform || "any");
      }
    } catch (_) { toast.error("Identité IA locale indisponible : aucune nouvelle génération autorisée."); }
  }, [user]);



  const submit = async (e) => {
    e.preventDefault();
    if (!canEdit || loading || (!ai.allowed && !["generated", "saving"].includes(operation?.state)) || hook.trim().length < 3) return;
    setLoading(true);
    try {
      const data = await submitAI(user, "remix", "/hooks/remix", { hook, niche, platform: platform === "any" ? "" : platform }, { standard: ai.standard });
      setCurrent(data);
      loadHistory();
    } catch (err) {
      toast.error(formatError(err));
      refreshOperation();
    } finally {
      setLoading(false);
      ai.read.reload().catch(() => {});
    }
  };

  const remove = async (id) => {
    if (!canEdit) return;
    try {
      await api.delete(`/hooks/remix/${id}`);
      if (current?.id === id) setCurrent(null);
      loadHistory();
    } catch (error) { toast.error(formatError(error)); }
  };

  return (
    <div className="space-y-6" data-testid="hook-remix-page">
      <SectionTabs tabs={SECTION_TABS.studio} activeKey="hook-remix" ariaLabel="Studio sections" data-testid="section-tabs-studio" />
      <div>
        <div className="flex items-center gap-2">
          <Wand2 className="h-6 w-6 text-primary" />
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Hook Remix</h1>
        </div>
        <p className="text-muted-foreground text-sm mt-1">
          Paste your opening line. Claude scores it and rewrites it in the patterns catching fire this week.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <AIState read={ai.read} scope="remix" operation={operation} onRefresh={refreshOperation} />
          {canEdit && <Card className="p-5" data-testid="remix-form-card">
            <form onSubmit={submit} className="space-y-3">
              <Textarea
                value={hook}
                onChange={(e) => setHook(e.target.value)}
                placeholder='e.g. "I spent $10,000 testing every productivity app so you don&apos;t have to"'
                rows={3}
                maxLength={400}
                className="text-base"
                data-testid="remix-hook-input"
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <Input value={niche} onChange={(e) => setNiche(e.target.value)} placeholder="Niche (optional)" className="sm:w-56" data-testid="remix-niche-input" />
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger className="sm:w-44" data-testid="remix-platform-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any platform</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="instagram">Instagram Reels</SelectItem>
                    <SelectItem value="youtube">YouTube Shorts</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex-1" />
                <span className="self-center font-mono text-xs text-muted-foreground">{hook.length}/400</span>
                <Button type="submit" disabled={loading || (!ai.allowed && !["generated", "saving"].includes(operation?.state)) || hook.trim().length < 3} data-testid="remix-submit-button">
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Remixing…</> : <><Wand2 className="h-4 w-4 mr-2" />Remix with Claude</>}
                </Button>
              </div>
            </form>
          </Card>}

          {current ? (
            <RemixResult item={current} />
          ) : (
            <Card className="p-10 text-center text-muted-foreground" data-testid="remix-empty">
              Aucun remix sélectionné. Consultez l’historique ou utilisez le formulaire si vos accès le permettent.
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card className="p-5" data-testid="remix-hot-patterns">
            <h3 className="font-display font-bold text-base flex items-center gap-2 mb-1"><Flame className="h-4 w-4 text-[hsl(var(--rose))]" /> Hot this week</h3>
            <p className="text-xs text-muted-foreground mb-3">Patterns Claude will steer your rewrites toward.</p>
            <ReadState read={hotRead} scope="hook-remix-hot" empty={!hot.length} title="Aucun pattern disponible cette semaine">
            <div className="space-y-2">
              {hot.map((p, i) => (
                <div key={p.name} className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-xs text-muted-foreground w-4">{i + 1}</span>
                  <span className="flex-1 truncate font-medium">{p.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{p.share_pct}%</span>
                </div>
              ))}

            </div>
            </ReadState>
          </Card>

          <Card className="p-5" data-testid="remix-history">
            <h3 className="font-display font-bold text-base mb-3">Recent remixes</h3>
            <ReadState read={historyRead} scope="hook-remix-history" empty={!history.length} title="Aucun remix dans l’historique">
            <div className="space-y-1">

              {history.map((h) => (
                <div key={h.id} className={classNames("group flex items-start gap-2 rounded-md px-2 py-2 hover:bg-accent transition-colors", current?.id === h.id && "bg-accent")}>
                  <button onClick={() => setCurrent(h)} className="flex-1 text-left min-w-0" data-testid={`remix-history-${h.id}`}>
                    <div className="text-sm line-clamp-2">{h.hook}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2">
                      <span className="font-mono font-bold text-primary">{h.result?.score}/10</span>
                      <span>{h.result?.detected_style}</span>
                      <span className="ml-auto">{timeAgo(h.created_at)}</span>
                    </div>
                  </button>
                  {canEdit && <button onClick={() => remove(h.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-[hsl(var(--rose))] transition-opacity mt-1" aria-label="Delete" data-testid={`remix-delete-${h.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>}
                </div>
              ))}
            </div>
            </ReadState>
          </Card>
        </div>
      </div>
    </div>
  );
}
